import React from 'react';
import { PropTypes } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Col, Collapse, Container, Row, UncontrolledTooltip } from 'reactstrap';
import { LanguageCode } from '@payer-admin-ui/api/src/invoices/models/LanguageCode';
import { DeliveryType } from '@payer-admin-ui/api/src/invoices/models/DeliveryType';
import { Validators } from '@payer-admin-ui/components-v2/src/inputs/validation';
import { RadioButtonGroup } from '../../RadioButton/RadioButtonGroup';
import { CountryDropdown } from '../../Dropdowns/CountryDropdown';
import { LanguageDropdown } from '../../Dropdowns/LanguageDropdown';
import { ExpandableContainer } from '../../ExpandableContainer/ExpandableContainer';
import { TextInput } from '../../Input/TextInput';
import styles from './InvoiceCustomerCard.module.scss';
import { RegNumberField } from './RegNumberField';

const InvoiceCustomerCard = ({
  customer,
  patchCustomer,
  deliveryType,
  disabled,
  addressFetchState,
  getAddress,
  resetGetAddressSuccess,
  expanded,
}) => {
  const { t } = useTranslation();

  const {
    customerNumber,
    companyName,
    customerType = companyName !== null ? 'ORGANISATION' : 'PRIVATE',
    address,
    regNumber,
    customerVatId,
  } = customer ?? {};

  const isEInvoice = deliveryType === DeliveryType.EINVOICE;
  const regNumberOrCustomerVatIdIsSpecified = regNumber || customerVatId;

  return (
    <ExpandableContainer
      header={<b>{t('components.cards.invoice.customer.header')}</b>}
      defaultExpanded={expanded ?? true}
    >
      {customer && (
        <Container className={styles.InvoiceCustomerCard}>
          <Row className={styles.section}>
            <Col>
              <RadioButtonGroup
                items={t('enums.customerType.values', { returnObjects: true })}
                title={<h5>{t('components.cards.invoice.customer.type')}</h5>}
                selectedItem={customerType}
                setSelectedItem={customerType => patchCustomer({ customerType })}
                disabled={disabled}
                inline
              />
            </Col>
          </Row>
          <Row className={styles.section}>
            <Col xs="6">
              <h5>{t('components.cards.invoice.customer.country')}</h5>
              <CountryDropdown
                selectedCountry={address?.countryCode}
                setSelectedCountry={countryCode => patchCustomer({ address: { countryCode } })}
                validators={[Validators.notEmpty]}
                disabled={disabled}
              />
            </Col>
            <Col xs="6">
              <h5>{t('components.cards.invoice.customer.language')}</h5>
              <LanguageDropdown
                languages={[
                  LanguageCode.da,
                  LanguageCode.nl,
                  LanguageCode.en,
                  LanguageCode.fr,
                  LanguageCode.de,
                  LanguageCode.es,
                  LanguageCode.sv,
                ]}
                selectedLanguage={address?.languageCode}
                setSelectedLanguage={languageCode => patchCustomer({ address: { languageCode } })}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row className={styles.section}>
            <Col>
              <TextInput
                title={<h5>{t('invoices.fields.customerNumber')}</h5>}
                value={customerNumber}
                setValue={customerNumber => patchCustomer({ customerNumber })}
                validators={[Validators.length(0, 48)]}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row className={styles.section} />
          <Row>
            <Col xs="12" sm="6">
              <RegNumberField
                countryCode={address?.countryCode}
                value={regNumber}
                setValue={regNumber => patchCustomer({ regNumber })}
                deliveryType={deliveryType}
                customerVatId={customerVatId}
                disabled={disabled}
                addressFetchState={addressFetchState}
                getAddress={getAddress}
                resetGetAddressSuccess={resetGetAddressSuccess}
              />
            </Col>
            <Col xs="12" sm="6">
              <Container className="p-0 m-0">
                <Row>
                  <Col>
                    <h5>{t('invoices.fields.vatId')}</h5>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <TextInput
                      value={customerVatId}
                      setValue={customerVatId => patchCustomer({ customerVatId })}
                      validators={[
                        ...(regNumberOrCustomerVatIdIsSpecified ? [] : [Validators.notEmpty]),
                        Validators.length(0, 48),
                      ]}
                      disabled={disabled}
                    />
                  </Col>
                </Row>
              </Container>
            </Col>
          </Row>
          <Row className={styles.section} />
          <Row className={styles.section}>
            <Col xs="12">
              <h5>{t('components.cards.invoice.customer.invoiceAddress')}</h5>
            </Col>
            <Col xs="12" sm="6">
              <Collapse isOpen={customerType === 'ORGANISATION'}>
                <TextInput
                  title={t('address.companyName')}
                  value={address?.companyName}
                  setValue={companyName => patchCustomer({ address: { companyName } })}
                  validators={[
                    ...(customerType === 'ORGANISATION' ? [Validators.notEmpty] : []),
                    Validators.length(0, 128),
                  ]}
                  disabled={disabled}
                />
              </Collapse>
            </Col>
            <Col xs="12" sm="6">
              <Collapse isOpen={customerType === 'ORGANISATION'}>
                <TextInput
                  title={t('address.yourReference')}
                  value={address?.yourReference}
                  setValue={yourReference => patchCustomer({ address: { yourReference } })}
                  validators={[
                    ...(customerType === 'ORGANISATION' ? [Validators.notEmpty] : []),
                    Validators.length(0, 48),
                  ]}
                  disabled={disabled}
                />
              </Collapse>
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('address.firstName')}
                value={address?.firstName}
                setValue={firstName => patchCustomer({ address: { firstName } })}
                validators={[
                  ...(customerType !== 'ORGANISATION' ? [Validators.notEmpty] : []),
                  Validators.length(0, 48),
                ]}
                disabled={disabled}
              />
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('address.lastName')}
                value={address?.lastName}
                setValue={lastName => patchCustomer({ address: { lastName } })}
                validators={[
                  ...(customerType !== 'ORGANISATION' ? [Validators.notEmpty] : []),
                  Validators.length(0, 48),
                ]}
                disabled={disabled}
              />
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('address.streetAddress1')}
                value={address?.streetAddress1}
                setValue={streetAddress1 => patchCustomer({ address: { streetAddress1 } })}
                validators={[Validators.notEmpty, Validators.length(0, 128)]}
                disabled={disabled}
              />
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('address.streetAddress2')}
                value={address?.streetAddress2}
                setValue={streetAddress2 => patchCustomer({ address: { streetAddress2 } })}
                validators={[Validators.length(0, 128)]}
                disabled={disabled}
              />
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('address.zipCode')}
                value={address?.zipCode}
                setValue={zipCode => patchCustomer({ address: { zipCode } })}
                validators={[Validators.notEmpty, Validators.length(0, 20)]}
                disabled={disabled}
              />
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('address.city')}
                value={address?.city}
                setValue={city => patchCustomer({ address: { city } })}
                validators={[Validators.notEmpty, Validators.length(0, 48)]}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row className={styles.section}>
            <Col xs="12" sm="6">
              <TextInput
                title={t('address.emailAddress')}
                value={address?.emailAddress}
                setValue={emailAddress => patchCustomer({ address: { emailAddress } })}
                validators={[Validators.validIfEmpty, Validators.length(0, 128), Validators.email]}
                disabled={disabled}
              />
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('address.phoneNumber')}
                value={address?.phoneNumber}
                setValue={phoneNumber => patchCustomer({ address: { phoneNumber: phoneNumber || null } })}
                validators={[Validators.length(0, 128)]}
                disabled={disabled}
              />
            </Col>
          </Row>
          {isEInvoice && (
            <Row className={styles.section}>
              <Col xs="12" sm="6">
                <div id="gln-input-field">
                  <TextInput
                    title={`${t('invoices.fields.eInvoice')} ${t('address.gln')}`}
                    value={address?.gln}
                    setValue={gln => patchCustomer({ address: { gln } })}
                    validators={[Validators.validIfEmpty, Validators.length(0, 128), Validators.number]}
                    disabled={disabled}
                  />
                  <UncontrolledTooltip placement="top" target="gln-input-field" trigger="hover" delay={800}>
                    {t('invoices.mouseHovers.gln')}
                  </UncontrolledTooltip>
                </div>
              </Col>
            </Row>
          )}
        </Container>
      )}
    </ExpandableContainer>
  );
};

InvoiceCustomerCard.propTypes = {
  customer: PropTypes.shape({
    companyName: PropTypes.string,
    address: PropTypes.object,
    regNumber: PropTypes.string,
  }),
  patchCustomer: PropTypes.func,
  deliveryType: PropTypes.string,
  disabled: PropTypes.bool,
  addressFetchState: PropTypes.object,
  getAddress: PropTypes.func,
  resetGetAddressSuccess: PropTypes.func,
  expanded: PropTypes.bool,
};

export { InvoiceCustomerCard };
